import { readdir, readFile } from "node:fs/promises";
import { basename, extname, join } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type { Pitfall, PitfallFrontmatter } from "./types.js";

/**
 * Directory-backed pitfall catalog. One `.md` file per pitfall.
 *
 * The catalog reads all `.md` files in `dir` on each query (no caching).
 * Callers that need cached behavior should wrap or call `list()` once and
 * reuse the result.
 */
export class PitfallCatalog {
  constructor(readonly dir: string) {}

  /** Load every pitfall in the directory, sorted by id. */
  async list(): Promise<readonly Pitfall[]> {
    const files = await this.entries();
    const out: Pitfall[] = [];
    for (const file of files) {
      out.push(await this.loadFile(file));
    }
    return out.sort((a, b) => a.id.localeCompare(b.id));
  }

  /** Load a single pitfall by id. Returns `undefined` if not found. */
  async get(id: string): Promise<Pitfall | undefined> {
    const all = await this.list();
    return all.find((p) => p.id === id);
  }

  /** Return only pitfalls matching the given category. */
  async filterByCategory(category: string): Promise<readonly Pitfall[]> {
    const all = await this.list();
    return all.filter((p) => p.category === category);
  }

  private async entries(): Promise<string[]> {
    try {
      const names = await readdir(this.dir);
      return names
        .filter((n) => n.endsWith(".md"))
        .map((n) => join(this.dir, n));
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
  }

  private async loadFile(path: string): Promise<Pitfall> {
    const raw = await readFile(path, "utf8");
    const { frontmatter, body } = parseFrontmatter<PitfallFrontmatter>(raw);
    const idFromFile = basename(path, extname(path));
    return {
      id: frontmatter.id ?? idFromFile,
      title: frontmatter.title,
      category: frontmatter.category,
      severity: frontmatter.severity,
      signal: frontmatter.signal,
      cause: frontmatter.cause,
      mitigation: frontmatter.mitigation,
      body: body.trimStart(),
    };
  }
}
